import React, { useEffect, useMemo, useState } from 'react';
import { Stack } from '@fluentui/react/lib/Stack';
import {
    ComboBox,
    DatePicker,
    DayOfWeek,
    DetailsList,
    Dialog,
    DialogType,
    IColumn,
    IDialogContentProps,
    PrimaryButton,
    SelectionMode,
    Selection,
    DialogFooter,
    DefaultButton,
} from '@fluentui/react';
import { _CountryOptions, _YearOptions } from './CountryAndYeartLists';
import { CalendarHolidays } from '../../../../../api/generated/data-contracts';
import { useQuery } from '@tanstack/react-query';
import { HOLIDAYS_BY_COUNTRY } from '../../../../../constants/queryKeys';
import { ApiCalls } from '../../../../../api/api';

const _listColumns = [
    { key: 'name', name: 'Name', fieldName: 'name', minWidth: 150, maxWidth: 150, isResizable: true },
    { key: 'date', name: 'Date', fieldName: 'date', minWidth: 150, maxWidth: 150, isResizable: false },
];

interface AddFromAPIDialogProps {
    hidden: boolean;
    onDismiss: (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;
    onAddHolidaysFromDialog: (newHolidays: CalendarHolidays[]) => any;
}
function AddFromAPIDialog(props: AddFromAPIDialogProps) {
    const holidaysSelectionModel = useMemo<Selection>(() => new Selection({ onSelectionChanged: () => setSelectedNewHolidays(getSelectedNewHolidays()) }), []);
    const [selectedNewHolidays, setSelectedNewHolidays] = useState<CalendarHolidays[]>([]);

    const [countryBoxSelection, setCountryBoxSelection] = useState<string>(null);
    const [yearBoxSelection, setYearBoxSelection] = useState<string>(null);
    const [hideDialog, setHideDialog] = useState<boolean>(props.hidden);

    const { data: newHolidays } = useQuery({
        queryKey: [HOLIDAYS_BY_COUNTRY],
        queryFn: () => ApiCalls.configurationCalendarsHolidaysList({ country: countryBoxSelection, year: Number(yearBoxSelection) }).then(res => res.data),
        enabled: Boolean(countryBoxSelection && yearBoxSelection),
    });

    useEffect(() => {
        if (newHolidays == null || newHolidays?.length < 1 || holidaysSelectionModel == null) {
            return;
        }
        holidaysSelectionModel.setAllSelected(true);
    }, [holidaysSelectionModel, newHolidays]);

    useEffect(() => {
        setHideDialog(props.hidden);
    }, [props.hidden]);

    const getSelectedNewHolidays = (): CalendarHolidays[] => {
        return holidaysSelectionModel.getSelection() as CalendarHolidays[];
    };

    const renderItemColumn = (item: any, index: number, column: IColumn) => {
        switch (column.key) {
            case 'date':
                const fieldContDate = new Date(item.date);
                return <DatePicker disabled firstDayOfWeek={DayOfWeek.Monday} value={fieldContDate} styles={{ textField: { width: 150 } }} />;
            default:
                const fieldContDefaultString = item[column.fieldName as keyof any] as string;
                return <span>{fieldContDefaultString}</span>;
        }
    };

    return (
        <Dialog
            hidden={hideDialog}
            onDismiss={props.onDismiss}
            minWidth={300}
            maxWidth={1000}
            dialogContentProps={
                {
                    type: DialogType.largeHeader,
                    title: 'Select country and year',
                    subText: 'Then check the holidays to add to the calendar, and click "Add"',
                } as IDialogContentProps
            }
        >
            <Stack tokens={{ childrenGap: 20 }} horizontal verticalAlign="baseline">
                <Stack.Item grow={1}>
                    <ComboBox
                        options={_CountryOptions}
                        autoComplete={'on'}
                        selectedKey={countryBoxSelection}
                        onChange={(e, o, i, v) => {
                            setCountryBoxSelection(o.key as string);
                        }}
                        styles={{ optionsContainer: { width: 191 } }}
                    />
                </Stack.Item>
                <Stack.Item grow={1}>
                    <ComboBox
                        options={_YearOptions}
                        autoComplete={'on'}
                        selectedKey={yearBoxSelection}
                        onChange={(e, o, i, v) => {
                            setYearBoxSelection(o.key as string);
                        }}
                        styles={{ optionsContainer: { width: 191 } }}
                    />
                </Stack.Item>
            </Stack>

            {newHolidays?.length > 1 && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <DetailsList
                        compact
                        columns={_listColumns}
                        onRenderItemColumn={renderItemColumn}
                        items={newHolidays}
                        selectionMode={SelectionMode.multiple}
                        selectionPreservedOnEmptyClick={true}
                        selection={holidaysSelectionModel}
                    />
                </Stack>
            )}

            <DialogFooter>
                <PrimaryButton onClick={() => props.onAddHolidaysFromDialog(selectedNewHolidays)} text="Add" />
                <DefaultButton onClick={props.onDismiss} text="Cancel" />
            </DialogFooter>
        </Dialog>
    );
}

export default AddFromAPIDialog;
